import {
  FaBookOpen,
  FaBoxes,
  FaBullhorn,
  FaCalculator,
  FaChartLine,
  FaCheckCircle,
  FaClipboardList,
  FaCode,
  FaDatabase,
  FaFileAlt,
  FaGraduationCap,
  FaHeadset,
  FaLayerGroup,
  FaMapMarkerAlt,
  FaNetworkWired,
  FaNewspaper,
  FaPenFancy,
  FaPlug,
  FaRocket,
  FaSearch,
  FaShoppingBag,
  FaStore,
  FaSyncAlt,
  FaTags,
  FaTruck,
  FaUpload,
  FaUsers,
  FaWordpress,
} from 'react-icons/fa';
import {
  EDUCATION_WEBSITE_PAGE,
  ERP_DEVELOPMENT_PAGE,
  MARKETPLACE_CATALOGING_PAGE,
} from './serviceLandingPages';

/** Homepage preview copy aligned with dedicated landing pages. */
export const HOME_ERP_PREVIEW = {
  eyebrow: ERP_DEVELOPMENT_PAGE.eyebrow,
  title: 'One connected flow from seller portal to profit intelligence.',
  lead:
    'We build custom commerce ERP platforms: seller and admin portals, multi-channel integrations, inventory and order automation, reconciliation, accounting exports, and analytics your operations team can trust.',
  badges: ERP_DEVELOPMENT_PAGE.badges,
  features: ERP_DEVELOPMENT_PAGE.deliverables,
  primaryCta: { to: '/erp-development', label: 'Explore ERP development' },
  secondaryCta: { to: '/getintouch', label: 'Plan your ERP build' },
};

export const HOME_MARKETPLACE_PREVIEW = {
  eyebrow: MARKETPLACE_CATALOGING_PAGE.eyebrow,
  title: 'Marketplace-ready product catalogs from raw data to live listings.',
  lead: MARKETPLACE_CATALOGING_PAGE.lead,
  badges: MARKETPLACE_CATALOGING_PAGE.badges,
  chips: MARKETPLACE_CATALOGING_PAGE.deliverables,
  primaryCta: { to: '/marketplace-cataloging', label: 'Marketplace cataloging services' },
  secondaryCta: { to: '/ecommerce-development', label: 'Ecommerce development' },
};

export const HOME_EDUCATION_PREVIEW = {
  eyebrow: EDUCATION_WEBSITE_PAGE.eyebrow,
  title: 'Education websites and course discovery platforms for institutions.',
  lead: EDUCATION_WEBSITE_PAGE.lead,
  badges: EDUCATION_WEBSITE_PAGE.badges,
  chips: EDUCATION_WEBSITE_PAGE.deliverables,
  primaryCta: { to: '/education-website-development', label: 'Education website development' },
  secondaryCta: { to: '/wordpress-development', label: 'WordPress development' },
};

export const ERP_FLOW_STEPS = [
  {
    id: 'portal',
    title: 'Seller and admin portals',
    desc: 'Role-based panels with catalog tools, order views, approvals, and notifications.',
    Icon: FaUsers,
  },
  {
    id: 'channels',
    title: 'Multi-channel integration',
    desc: 'Connect marketplace, webstore, offline, dealer, and distribution channels in one stack.',
    Icon: FaNetworkWired,
  },
  {
    id: 'inventory',
    title: 'Catalog and inventory',
    desc: 'Centralize stock, product data, channel sync, and availability updates.',
    Icon: FaBoxes,
  },
  {
    id: 'orders',
    title: 'Orders and fulfillment',
    desc: 'Automate bulk processing, shipments, returns, and exception handling.',
    Icon: FaClipboardList,
  },
  {
    id: 'reconcile',
    title: 'Reconciliation and claims',
    desc: 'Payment matching, short-payment checks, and claim-ready recovery reports.',
    Icon: FaSyncAlt,
  },
  {
    id: 'profit',
    title: 'Profit and analytics',
    desc: 'Margin tools, charge analysis, alerts, and reporting leadership can trust.',
    Icon: FaChartLine,
  },
];

export const ERP_CHANNEL_POINTS = [
  { label: 'Marketplaces', Icon: FaStore },
  { label: 'Webstore', Icon: FaNetworkWired },
  { label: 'Dealers', Icon: FaUsers },
  { label: 'Warehouse', Icon: FaBoxes },
  { label: 'Shipping', Icon: FaTruck },
  { label: 'Finance', Icon: FaCalculator },
];

export const ERP_DASHBOARD_METRICS = [
  { label: 'Orders today (sample)', value: '1,248', status: 'good' },
  { label: 'Active channels (sample)', value: '8', status: 'good' },
  { label: 'Inventory sync (sample)', value: 'Live', status: 'good' },
  { label: 'Reconciliation (sample)', value: '98.4%', status: 'warn' },
  { label: 'Claims ready (sample)', value: '37', status: 'alert' },
  { label: 'Net margin (sample)', value: '+12.6%', status: 'good' },
];

export const MARKETPLACE_CATALOGING_CHANNELS = [
  'Amazon',
  'Flipkart',
  'Meesho',
  'Myntra',
  'Ajio',
  'JioMart',
  'Shopify',
  'Custom B2B',
];

export const MARKETPLACE_CATALOGING_FLOW = [
  { title: 'Product data', desc: 'SKU sheets, images, specs', Icon: FaFileAlt },
  { title: 'Content build', desc: 'Titles, bullets, SEO copy', Icon: FaTags },
  { title: 'Template mapping', desc: 'Channel-wise upload sheets', Icon: FaLayerGroup },
  { title: 'QC and upload', desc: 'Validation, errors, approvals', Icon: FaUpload },
  { title: 'Live optimization', desc: 'Updates, fixes, visibility', Icon: FaChartLine },
];

export const MARKETPLACE_CATALOGING_SERVICES = MARKETPLACE_CATALOGING_PAGE.services.map((service, index) => ({
  ...service,
  Icon: [FaFileAlt, FaLayerGroup, FaSearch, FaCheckCircle][index] || FaTags,
}));

export const EDUCATION_PLATFORM_FLOW = [
  {
    id: 'plan',
    title: 'Plan',
    desc: 'Align goals, audiences, content models, and rollout priorities.',
    Icon: FaClipboardList,
  },
  {
    id: 'design',
    title: 'Design',
    desc: 'Shape templates, navigation, and conversion paths.',
    Icon: FaPenFancy,
  },
  {
    id: 'build',
    title: 'Build',
    desc: 'Implement Sage/WordPress themes, flexible sections, and reusable blocks.',
    Icon: FaCode,
  },
  {
    id: 'integrate',
    title: 'Integrate',
    desc: 'Connect forms, search, course data, analytics, and embeds.',
    Icon: FaPlug,
  },
  {
    id: 'launch',
    title: 'Launch',
    desc: 'Support QA, content migration, campaign pages, and go-live readiness.',
    Icon: FaRocket,
  },
  {
    id: 'support',
    title: 'Support',
    desc: 'Maintain performance, updates, new sections, and ongoing improvements.',
    Icon: FaHeadset,
  },
];

export const EDUCATION_PLATFORM_HUB_NODES = [
  { label: 'Courses', Icon: FaBookOpen },
  { label: 'Campuses', Icon: FaMapMarkerAlt },
  { label: 'Events', Icon: FaBullhorn },
  { label: 'Campaigns', Icon: FaRocket },
  { label: 'Forms', Icon: FaFileAlt },
  { label: 'Search', Icon: FaSearch },
  { label: 'Stories', Icon: FaNewspaper },
  { label: 'Analytics', Icon: FaChartLine },
];

export const EDUCATION_PLATFORM_SERVICES = EDUCATION_WEBSITE_PAGE.services.map((service, index) => ({
  ...service,
  Icon: [FaWordpress, FaLayerGroup, FaGraduationCap, FaBullhorn, FaSearch, FaPlug][index] || FaGraduationCap,
}));

export const MARKETPLACE_GRAPHIC = {
  hubTitle: 'Cataloging hub',
  hubDesc: 'Content, images, attributes, pricing, inventory fields, and marketplace rules aligned before upload.',
  HubIcon: FaShoppingBag,
};

export const ERP_GRAPHIC = {
  coreTitle: 'ERP core',
  coreDesc: 'Shared data layer for orders, stock, payments, claims, and reports.',
  CoreIcon: FaDatabase,
};
